import {
  ECalculatedFieldFunction,
  EDateDiffPart,
  EGenericSelectDataGroupBy,
  ESortType,
  ESymbolCategory,
  IDatabaseTableModel,
  IGenericSelectCalculatedField,
  IGenericSelectDataField,
  IGenericSelectDataModel,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu, {
  KuikaDropdownMenuItemType
} from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTable, { KuikaTableColumn } from "../../../../ui-kit/kuika-table/kuika-table";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import { CalculatedFieldsItemComponent } from "./calculated-fields-item";

export interface IFieldsState {}

export interface IFieldsProps {
  tables: IDatabaseTableModel[];
  selectData: IGenericSelectDataModel;
  updateSelectData: (newData: IGenericSelectDataModel) => void;
  getTypeIcon: (type: string) => React.ReactElement;
  isModal?: boolean;
}

const groupByOptions = [
  { value: EGenericSelectDataGroupBy.Count, label: "Count" },
  { value: EGenericSelectDataGroupBy.Min, label: "Min" },
  { value: EGenericSelectDataGroupBy.Max, label: "Max" }
];

class Fields extends React.PureComponent<IFieldsProps, IFieldsState & { editingRows: Record<number, boolean> }> {
  constructor(props: IFieldsProps) {
    super(props);
    this.state = {
      editingRows: {}
    };
  }

  // Regular field handlers
  handleUpdateData = (newData: IGenericSelectDataField, idx: number) => {
    const selectData = _.cloneDeep(this.props.selectData);
    selectData.fields[idx] = newData;
    this.props.updateSelectData(selectData);
  };

  handleClickAddField = () => {
    const selectData = _.cloneDeep(this.props.selectData);
    selectData.fields.push({
      tableName: "",
      tableSchema: "",
      colName: "",
      colSqlDataType: "",
      alias: ""
    });
    this.props.updateSelectData(selectData);
  };

  handleDelete = (idx: number) => {
    const selectData = _.cloneDeep(this.props.selectData);
    selectData.fields.splice(idx, 1);
    this.props.updateSelectData(selectData);
  };

  getSelectedTableColumns = (tableName: string) => {
    const targetTable = this.props.tables.find((x) => x.name === tableName);
    return targetTable?.columns ?? [];
  };

  handleTableSelect = (tableName: string, idx: number) => {
    const data = _.cloneDeep(this.props.selectData.fields[idx]);
    data.tableName = tableName;
    data.tableId =
      this.props.tables.find((x) => x.name === tableName)?.id == ""
        ? null
        : this.props.tables.find((x) => x.name === tableName)?.id;
    data.colName = "";
    data.groupBy = undefined;
    this.handleUpdateData(data, idx);
  };

  getTypeName = (sqlDataType: string, dataLength: number | any) => {
    let potantialType = KMainFunctions.getTypeNameBySqlDataType(sqlDataType);
    if (potantialType == "Unknown") {
      potantialType = KMainFunctions.getTypeNameBySqlDataType(`${sqlDataType}(${dataLength})`);
    }
    return potantialType;
  };

  handleColumnSelect = (colName: string, idx: number) => {
    const data = _.cloneDeep(this.props.selectData.fields[idx]);
    data.colName = colName;
    data.colSqlDataType = "";
    data.groupBy = undefined;
    const selectedTable = this.props.tables.find((x) => x.name === data.tableName);

    if (selectedTable) {
      const selectedColumn = selectedTable.columns.find((x) => x.name === colName);
      if (selectedColumn) {
        data.tableSchema = selectedTable.schema;
        data.colSqlDataType = selectedColumn.sqlDataType;
        data.colSqlDataType = this.getTypeName(selectedColumn.sqlDataType, selectedColumn.maxLength);
      }
    }
    this.handleUpdateData(data, idx);
  };

  handleSelectGroupBy = (value: EGenericSelectDataGroupBy | undefined, idx: number) => {
    const data = _.cloneDeep(this.props.selectData.fields[idx]);
    data.groupBy = value;
    this.handleUpdateData(data, idx);
  };

  handleAliasChange = (value: string, idx: number) => {
    const data = _.cloneDeep(this.props.selectData.fields[idx]);
    data.alias = value;
    this.handleUpdateData(data, idx);
  };

  // Calculated field handlers
  handleUpdateCalculatedField = (newData: IGenericSelectCalculatedField, idx: number) => {
    const selectData = _.cloneDeep(this.props.selectData);
    if (!selectData.calculatedFields) {
      selectData.calculatedFields = [];
    }
    selectData.calculatedFields[idx] = newData;
    this.props.updateSelectData(selectData);
  };

  createEmptyField = (withSortType: boolean = false): IGenericSelectDataField => ({
    tableName: "",
    tableSchema: "",
    tableId: null,
    colName: "",
    colSqlDataType: "",
    alias: "",
    paramSymbol: { category: ESymbolCategory.fixed } as ISymbol,
    ...(withSortType ? { sortType: ESortType.ascending } : {})
  });

  handleClickAddCalculatedField = (func?: ECalculatedFieldFunction) => {
    const selectData = _.cloneDeep(this.props.selectData);
    if (!selectData.calculatedFields) {
      selectData.calculatedFields = [];
    }

    const newField: IGenericSelectCalculatedField = {
      alias: "",
      function: func || (undefined as any),
      fields: [],
      partitionByFields: [],
      orderByFields: [],
      isCollapsed: false,
      dateDiffPart: undefined
    };

    // Initialize fields based on function requirements if function is selected
    if (func === ECalculatedFieldFunction.RowNumber) {
      newField.orderByFields = [this.createEmptyField(true)];
      // Validate partitionBy (optional) is empty initially
      newField.partitionByFields = [];
    } else if (func === ECalculatedFieldFunction.DateDiff) {
      newField.fields = [this.createEmptyField(), this.createEmptyField()];
      newField.dateDiffPart = EDateDiffPart.Day;
    }

    selectData.calculatedFields.push(newField);
    this.props.updateSelectData(selectData);

    const newFieldIndex = selectData.calculatedFields.length - 1;
    this.setState((prev) => ({
      editingRows: { ...prev.editingRows, [newFieldIndex]: true }
    }));

    const element = document.querySelector(".fields-area");
    if (element) {
      setTimeout(() => {
        element.scrollTo({
          top: element.scrollHeight + 100,
          behavior: "smooth"
        });
      }, 100);
    }
  };

  handleDeleteCalculatedField = (idx: number) => {
    const selectData = _.cloneDeep(this.props.selectData);
    if (!selectData.calculatedFields) {
      selectData.calculatedFields = [];
    }
    selectData.calculatedFields.splice(idx, 1);
    this.props.updateSelectData(selectData);
  };

  handleSelectCalculatedFieldGroupBy = (value: EGenericSelectDataGroupBy | undefined, idx: number) => {
    const selectData = _.cloneDeep(this.props.selectData);
    if (!selectData.calculatedFields) {
      selectData.calculatedFields = [];
    }
    selectData.calculatedFields[idx].groupBy = value;
    this.props.updateSelectData(selectData);
  };

  handleCalculatedFieldAliasChange = (value: string, idx: number) => {
    const selectData = _.cloneDeep(this.props.selectData);
    if (!selectData.calculatedFields) {
      selectData.calculatedFields = [];
    }
    selectData.calculatedFields[idx].alias = value;
    this.props.updateSelectData(selectData);
  };

  handleEditEndCalculatedField = (idx: number) => {
    this.setState((prev) => {
      const newEditingRows = { ...prev.editingRows };
      delete newEditingRows[idx];
      return { editingRows: newEditingRows };
    });
  };

  handleCollapseCalculatedField = (idx: number) => {
    const selectData = _.cloneDeep(this.props.selectData);
    if (!selectData.calculatedFields) {
      selectData.calculatedFields = [];
    }
    selectData.calculatedFields[idx].isCollapsed = true;
    this.props.updateSelectData(selectData);
  };

  handleEditCalculatedField = (idx: number) => {
    const selectData = _.cloneDeep(this.props.selectData);
    if (!selectData.calculatedFields) {
      selectData.calculatedFields = [];
    }
    // Toggle collapse state or ensure it is expanded
    const isCurrentlyCollapsed = selectData.calculatedFields[idx].isCollapsed !== false;
    selectData.calculatedFields[idx].isCollapsed = false; // Always expand

    // Set editing state for this row
    this.setState((prev) => ({
      editingRows: { ...prev.editingRows, [idx]: true }
    }));

    this.props.updateSelectData(selectData);
  };

  getUsedFunctions = (): ECalculatedFieldFunction[] => {
    const calculatedFields = this.props.selectData.calculatedFields || [];
    return calculatedFields.filter((cf) => cf.function !== undefined).map((cf) => cf.function);
  };

  canAddMoreCalculatedFields = (): boolean => {
    const usedFunctions = this.getUsedFunctions();
    const allFunctions = [ECalculatedFieldFunction.DateDiff, ECalculatedFieldFunction.RowNumber];
    // Can add more if not all functions are used
    return usedFunctions.length < allFunctions.length;
  };

  // Helper methods to check if any field has a validation error for a specific property
  hasTableNameValidationError = (): boolean => {
    const fields = this.props.selectData.fields || [];
    const calculatedFields = this.props.selectData.calculatedFields || [];

    // Check regular fields
    const hasRegularFieldError = fields.some((f) => f.validations?.tableName === false);

    // Check calculated fields and their nested fields
    const hasCalculatedFieldError = calculatedFields.some((cf) => {
      const hasMainError = cf.validations && Object.values(cf.validations as any).some((v) => v === false);
      const hasFieldsError = cf.fields?.some((f) => f.validations?.tableName === false);
      const hasOrderByError = cf.orderByFields?.some((f) => f.validations?.tableName === false);
      const hasPartitionByError = cf.partitionByFields?.some((f) => f.validations?.tableName === false);
      return hasMainError || hasFieldsError || hasOrderByError || hasPartitionByError;
    });

    return hasRegularFieldError || hasCalculatedFieldError;
  };

  hasColNameValidationError = (): boolean => {
    const fields = this.props.selectData.fields || [];
    const calculatedFields = this.props.selectData.calculatedFields || [];

    // Check regular fields
    const hasRegularFieldError = fields.some((f) => f.validations?.colName === false);

    // Check calculated fields and their nested fields
    const hasCalculatedFieldError = calculatedFields.some((cf) => {
      const hasFieldsError = cf.fields?.some((f) => f.validations?.colName === false);
      const hasOrderByError = cf.orderByFields?.some((f) => f.validations?.colName === false);
      const hasPartitionByError = cf.partitionByFields?.some((f) => f.validations?.colName === false);
      return hasFieldsError || hasOrderByError || hasPartitionByError;
    });

    return hasRegularFieldError || hasCalculatedFieldError;
  };

  hasGroupByValidationError = (): boolean => {
    const fields = this.props.selectData.fields || [];
    // Check if any field with groupBy has alias validation error
    return fields.some((f) => f.groupBy && f.validations?.alias === false);
  };

  hasAliasValidationError = (): boolean => {
    const fields = this.props.selectData.fields || [];
    const calculatedFields = this.props.selectData.calculatedFields || [];

    const hasRegularFieldError = fields.some((f) => f.validations?.alias === false);
    const hasCalculatedFieldError = calculatedFields.some((cf) => cf.validations?.alias === false);

    return hasRegularFieldError || hasCalculatedFieldError;
  };

  getValidationSuffixIcon = (hasError: boolean, hasValue: boolean) => {
    if (hasError && !hasValue) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <KuikaSvg name="alert-circle" width={16} height={16} color="var(--red-5)" />
          <KuikaSvg name="chevron-down" type="arrows" width={12} height={12} color="rgba(96, 111, 133, 1)" />
        </div>
      );
    }
    return undefined;
  };

  getValidationRightIcon = (hasError: boolean, hasValue: boolean) => {
    if (hasError && !hasValue) {
      return <KuikaSvg name="alert-circle" width={16} height={16} color="var(--red-5)" />;
    }
    return undefined;
  };

  // Helper to check if row is a calculated field
  isCalculatedFieldRow = (row: any): row is IGenericSelectCalculatedField & { _index: number; _isCalculated: true } => {
    return row._isCalculated === true;
  };

  // Get function name for display
  getFunctionName = (calc: IGenericSelectCalculatedField): string => {
    switch (calc.function) {
      case ECalculatedFieldFunction.DateDiff:
        return "DateDiff";
      case ECalculatedFieldFunction.RowNumber:
        return "Row Number";
      default:
        return "Function";
    }
  };

  // Get function signature for display
  getFunctionSignature = (calc: IGenericSelectCalculatedField): string => {
    const func = calc.function;

    if (func === ECalculatedFieldFunction.DateDiff) {
      const datePart = calc.dateDiffPart !== undefined ? calc.dateDiffPart : "Day";
      const startField = calc.fields?.[0];
      const endField = calc.fields?.[1];

      if (startField?.colName && endField?.colName) {
        const startTable = startField.tableAlias || startField.tableName || "";
        const endTable = endField.tableAlias || endField.tableName || "";
        return `DATEDIFF(${datePart}, ${startTable}.${startField.colName}, ${endTable}.${endField.colName})`;
      }
      return `DATEDIFF(${datePart}, ...)`;
    }

    if (func === ECalculatedFieldFunction.RowNumber) {
      const partitionFields = (calc.partitionByFields || []) as IGenericSelectDataField[];
      const orderFields = (calc.orderByFields || []) as IGenericSelectDataField[];

      let signature = "ROW_NUMBER() OVER (";

      if (partitionFields.length > 0) {
        const partitionParts = partitionFields
          .filter((f) => f.tableName && f.colName)
          .map((f) => {
            const table = f.tableAlias || f.tableName || "";
            return `${table}.${f.colName}`;
          });
        if (partitionParts.length > 0) {
          signature += `PARTITION BY ${partitionParts.join(", ")} `;
        }
      }

      if (orderFields.length > 0) {
        const orderParts = orderFields
          .filter((f) => f.tableName && f.colName)
          .map((f) => {
            const table = f.tableAlias || f.tableName || "";
            return `${table}.${f.colName}`;
          });
        if (orderParts.length > 0) {
          signature += `ORDER BY ${orderParts.join(", ")}`;
        }
      }

      signature += ")";
      return signature;
    }

    return "Select function...";
  };

  getColumns = (): KuikaTableColumn<any>[] => {
    return [
      {
        id: "tableName",
        header: () => "Field Type",
        cellClassName: (row) => {
          if (this.isCalculatedFieldRow(row)) {
            const hasError =
              (row as any).validations &&
              (Object.values((row as any).validations).some((v) => v === false) ||
                (row.fields &&
                  row.fields.some(
                    (f) => f.validations && Object.values(f.validations as any).some((v) => v === false)
                  )) ||
                (row.orderByFields &&
                  row.orderByFields.some(
                    (f) => f.validations && Object.values(f.validations as any).some((v) => v === false)
                  )) ||
                (row.partitionByFields &&
                  row.partitionByFields.some(
                    (f) => f.validations && Object.values(f.validations as any).some((v) => v === false)
                  )));
            return hasError ? "has-validation-error" : "";
          }
          return (row as any).validations?.tableName === false ? "has-validation-error" : "";
        },
        cell: (row, tableRow) => {
          // Calculated field - show function name with expander
          if (this.isCalculatedFieldRow(row)) {
            // Check if calculated field has any validation errors
            const hasCalcValidationError =
              ((row as any).validations && Object.values((row as any).validations).some((v) => v === false)) ||
              row.fields?.some((f) => f.validations && Object.values(f.validations as any).some((v) => v === false)) ||
              row.orderByFields?.some(
                (f) => f.validations && Object.values(f.validations as any).some((v) => v === false)
              ) ||
              row.partitionByFields?.some(
                (f) => f.validations && Object.values(f.validations as any).some((v) => v === false)
              );

            return (
              <div style={{ width: "100%", display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    marginRight: 8,
                    transform: tableRow.getIsExpanded() ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform 0.2s"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    tableRow.toggleExpanded();
                  }}
                >
                  <KuikaSvg name="chevron-right" type="arrows" width={16} height={16} color="var(--dark-7)" />
                </div>
                <div className="kuika-flex-between kuika-width-full">
                  <div className="kuika-flex-center--gap-xs">
                    <KuikaSvg
                      name={row.function === ECalculatedFieldFunction.DateDiff ? "calendar-event" : "layout-rows"}
                      width={16}
                      height={16}
                      color="var(--dark-7)"
                    />
                    <KuikaText style={{ color: "var(--dark-5)" }}>{this.getFunctionName(row)}</KuikaText>
                  </div>
                  {hasCalcValidationError && (
                    <KuikaSvg name="alert-circle" width={16} height={16} color="var(--red-5)" />
                  )}
                </div>
              </div>
            );
          }

          // Regular field - show table select
          const tableOptions = this.props.selectData.tables.map((x) => ({
            value: x.name,
            label: (
              <>
                <KuikaSvg name="table" width={16} height={16} />
                <KuikaText>{x.name}</KuikaText>
              </>
            )
          }));
          const hasValidationError = (row as any).validations?.tableName === false;
          return (
            <div style={{ width: "100%" }}>
              <KuikaSelectbox
                className="kuika-selectbox--no-border"
                searchable
                value={row.tableName}
                onChange={(val) => this.handleTableSelect(val, (row as any)._index)}
                options={tableOptions}
                placeholder="Select table"
                suffixIcon={this.getValidationSuffixIcon(hasValidationError, !!row.tableName)}
              />
            </div>
          );
        }
      },
      {
        id: "colName",
        header: () => "Field",
        cellClassName: (row) =>
          !this.isCalculatedFieldRow(row) && (row as any).validations?.colName === false ? "has-validation-error" : "",
        cell: (row) => {
          // Calculated field - show function signature
          if (this.isCalculatedFieldRow(row)) {
            return (
              <KuikaTooltip content={this.getFunctionSignature(row)} position="bottom">
                <div style={{ width: "100%", padding: "0 12px", overflow: "hidden" }}>
                  <KuikaText
                    style={{
                      color: "var(--dark-5)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block"
                    }}
                    title={this.getFunctionSignature(row)}
                  >
                    {this.getFunctionSignature(row)}
                  </KuikaText>
                </div>
              </KuikaTooltip>
            );
          }

          // Regular field - show column select
          const columnOptions = this.getSelectedTableColumns(row.tableName).map((x) => ({
            value: x.name,
            label: (
              <>
                {this.props.getTypeIcon(x.kuikaDataType)}
                <KuikaText>{x.name}</KuikaText>
              </>
            )
          }));
          const hasValidationError = (row as any).validations?.colName === false;
          return (
            <div style={{ width: "100%" }}>
              <KuikaSelectbox
                className="kuika-selectbox--no-border"
                searchable
                value={row.colName}
                onChange={(val) => this.handleColumnSelect(val, (row as any)._index)}
                options={columnOptions}
                placeholder="Select column"
                suffixIcon={this.getValidationSuffixIcon(hasValidationError, !!row.colName)}
              />
            </div>
          );
        }
      },
      {
        id: "groupBy",
        header: () => "Group By",
        cell: (row) => {
          // Calculated field - show group by (only if function supports it)
          if (this.isCalculatedFieldRow(row)) {
            const supportsGroupBy = row.function === ECalculatedFieldFunction.DateDiff;
            if (!supportsGroupBy) {
              return <div style={{ width: "100%", padding: "0 12px", color: "var(--dark-4)" }}>—</div>;
            }
            const hasValidationError = row.groupBy && (row as any).validations?.alias === false;
            return (
              <KuikaSelectbox
                className="kuika-selectbox--no-border"
                value={row.groupBy}
                onChange={(val) => this.handleSelectCalculatedFieldGroupBy(val, (row as any)._index)}
                options={groupByOptions}
                placeholder="Select"
                allowClear
                onClear={() => this.handleSelectCalculatedFieldGroupBy(undefined, (row as any)._index)}
                suffixIcon={this.getValidationSuffixIcon(!!hasValidationError, false)}
              />
            );
          }

          // Regular field
          const hasValidationError = row.groupBy && (row as any).validations?.alias === false;
          return (
            <KuikaSelectbox
              className="kuika-selectbox--no-border"
              value={row.groupBy}
              onChange={(val) => this.handleSelectGroupBy(val, (row as any)._index)}
              options={groupByOptions}
              placeholder="Select"
              allowClear
              onClear={() => this.handleSelectGroupBy(undefined, (row as any)._index)}
              suffixIcon={this.getValidationSuffixIcon(!!hasValidationError, false)}
            />
          );
        },
        width: 140
      },
      {
        id: "alias",
        header: () => "Alias",
        cellClassName: (row) => ((row as any).validations?.alias === false ? "has-validation-error" : ""),
        cell: (row, tableRow) => {
          // Calculated field
          if (this.isCalculatedFieldRow(row)) {
            const isExpanded = tableRow?.getIsExpanded?.() || false;
            const rowData = row as any;
            const hasValidationError = rowData.validations?.alias === false;
            return (
              <div style={{ width: "100%" }}>
                <KuikaInput
                  key={`calc-alias-${rowData._index}-${rowData.function || ""}`}
                  className="kuika-input--no-border"
                  placeholder="Enter Alias"
                  defaultValue={row.alias}
                  onBlur={(e) => this.handleCalculatedFieldAliasChange(e.target.value as string, rowData._index)}
                  disabled={isExpanded}
                  rightIcon={this.getValidationRightIcon(hasValidationError, !!row.alias)}
                />
              </div>
            );
          }

          // Regular field
          const rowData = row as any;
          const hasValidationError = rowData.validations?.alias === false;
          return (
            <div style={{ width: "100%" }}>
              <KuikaInput
                key={`field-alias-${rowData._index}-${row.tableName || ""}-${row.colName || ""}`}
                className="kuika-input--no-border"
                placeholder="Enter Alias"
                defaultValue={row.alias}
                onBlur={(e) => this.handleAliasChange(e.target.value as string, rowData._index)}
                rightIcon={this.getValidationRightIcon(hasValidationError, !!row.alias)}
              />
            </div>
          );
        },
        width: 160
      },
      {
        id: "actions",
        header: () => "",
        cell: (row) => {
          const original = row as any;
          // Calculated field - show 3-dot menu
          if (this.isCalculatedFieldRow(row)) {
            const menuOptions: KuikaDropdownMenuItemType[] = [
              {
                key: "edit",
                label: "Edit",
                icon: <KuikaSvg name="edit" width={16} height={16} />,
                onClick: () => {
                  // This will be handled by the expanded row logic or we can trigger it here
                  // For now, let's just make sure the row is expanded
                  this.handleEditCalculatedField(original._index);
                }
              },
              {
                key: "delete",
                label: "Delete",
                icon: <KuikaSvg name="trash" width={16} height={16} color="var(--red-5)" />,
                onClick: () => this.handleDeleteCalculatedField(original._index),
                danger: true
              }
            ];

            return (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <KuikaDropdownMenu options={menuOptions}>
                  {(open) => (
                    <KuikaButton isIcon type="tertiary" color="neutral" active={open}>
                      <KuikaSvg name="dots" width={16} height={16} />
                    </KuikaButton>
                  )}
                </KuikaDropdownMenu>
              </div>
            );
          }

          // Regular field
          return (
            <KuikaSvg
              name="trash"
              width={16}
              height={16}
              color="var(--red-5)"
              pointer
              onClick={() => this.handleDelete(original._index)}
            />
          );
        },
        width: 40,
        align: "center"
      }
    ];
  };

  render() {
    // Regular fields with index
    const regularFieldsData = this.props.selectData.fields.map((field, idx) => ({
      ...field,
      _index: idx,
      _isCalculated: false
    }));

    const calculatedFields = this.props.selectData.calculatedFields || [];
    const usedFunctions = this.getUsedFunctions();

    // Collapsed calculated fields (shown in table)
    const collapsedCalculatedFieldsData = calculatedFields.map((cf, idx) => {
      return {
        ...cf,
        _index: idx,
        _isCalculated: true
      };
    });

    // Combined table data: regular fields + collapsed calculated fields
    const tableData = [...regularFieldsData, ...collapsedCalculatedFieldsData];

    const dropdownOptions: KuikaDropdownMenuItemType[] = [
      {
        key: "field",
        label: "Field",
        onClick: this.handleClickAddField,
        icon: <KuikaSvg name="table" width={16} height={16} />
      }
    ];

    dropdownOptions.push({
      key: "datediff",
      label: "DateDiff",
      onClick: () => this.handleClickAddCalculatedField(ECalculatedFieldFunction.DateDiff),
      icon: <KuikaSvg name="calendar-event" width={16} height={16} />
    });

    dropdownOptions.push({
      key: "rownumber",
      label: "Row Number",
      onClick: () => this.handleClickAddCalculatedField(ECalculatedFieldFunction.RowNumber),
      icon: <KuikaSvg name="layout-rows" width={16} height={16} />
    });

    return (
      <>
        <KuikaTable
          data={tableData}
          columns={this.getColumns()}
          rowKey={(row: any) => (row._isCalculated ? `calc-field-${row._index}` : `field-${row._index}`)}
          enablePagination={false}
          emptyTitle="No fields"
          emptyDescription="Add a field to select columns from tables"
          showHeader={true}
          enableExpanding={true}
          getRowCanExpand={(row) => this.isCalculatedFieldRow(row)}
          expanded={calculatedFields.reduce(
            (acc, cf, idx) => {
              if (cf.isCollapsed === false) {
                acc[`calc-field-${idx}`] = true;
              }
              return acc;
            },
            {} as Record<string, boolean>
          )}
          onExpandedChange={(updater) => {
            // Convert updater to new expanded state
            const currentExpanded = calculatedFields.reduce(
              (acc, cf, idx) => {
                if (cf.isCollapsed === false) {
                  acc[`calc-field-${idx}`] = true;
                }
                return acc;
              },
              {} as Record<string, boolean>
            );

            const newExpanded = typeof updater === "function" ? updater(currentExpanded) : updater;

            const selectData = _.cloneDeep(this.props.selectData);
            if (!selectData.calculatedFields) return;

            selectData.calculatedFields.forEach((cf, idx) => {
              const key = `calc-field-${idx}`;
              const isExpanded = !!newExpanded[key];
              cf.isCollapsed = !isExpanded;
            });

            this.props.updateSelectData(selectData);
          }}
          renderSubComponent={(record) => {
            if (this.isCalculatedFieldRow(record)) {
              const usedFunctionsExcludingCurrent = usedFunctions.filter((f) => f !== record.function);
              return (
                <div style={{ padding: "0" }}>
                  <CalculatedFieldsItemComponent
                    key={`calc-field-expanded-${record._index}`}
                    data={record}
                    updateData={(newData) => this.handleUpdateCalculatedField(newData, record._index)}
                    tables={this.props.tables}
                    selectedTables={this.props.selectData.tables}
                    delete={() => this.handleDeleteCalculatedField(record._index)}
                    getTypeIcon={this.props.getTypeIcon}
                    usedFunctions={usedFunctionsExcludingCurrent}
                    allCalculatedFields={calculatedFields}
                    currentIndex={record._index}
                    defaultEditing={!!this.state.editingRows[record._index]}
                    onEditEnd={() => this.handleEditEndCalculatedField(record._index)}
                    onCollapse={() => this.handleCollapseCalculatedField(record._index)}
                  />
                </div>
              );
            }
            return null;
          }}
        />

        <div className="generic-sql__filter__buttons">
          <KuikaDropdownMenu options={dropdownOptions}>
            <KuikaButton type="primary">
              <KuikaSvg name="plus" width={16} height={16} />
              ADD FIELD
            </KuikaButton>
          </KuikaDropdownMenu>
        </div>
      </>
    );
  }
}

export const FieldsComponent = Fields;
